import React from "react";
import { ArrowLeft, User, Pencil, FilePlus, Save, Download, Upload, Folder, ChevronDown } from "lucide-react";
import { FaFloppyDisk } from "react-icons/fa6";
import { MdBackpack } from "react-icons/md";
import ProjectSizeInspector from "./ProjectSizeInspector";

// --- Kid-Friendly Tactile SVG Icons for Junior Mode ("jr") ---

const JrBackIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path
      d="M15 6L9 12L15 18"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const JrKidExplorerIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#FFD166" stroke="#F59E0B" strokeWidth="1.8" />
    <circle cx="9" cy="10.5" r="1.4" fill="#3D2610" />
    <circle cx="15" cy="10.5" r="1.4" fill="#3D2610" />
    <path
      d="M8.5 14.5C9.3 16.2 11 17 12 17C13 17 14.7 16.2 15.5 14.5"
      stroke="#3D2610"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="7" cy="13" r="1.2" fill="#FF8FA3" opacity="0.7" />
    <circle cx="17" cy="13" r="1.2" fill="#FF8FA3" opacity="0.7" />
  </svg>
);

const JrFolderIcon = () => (
  <svg className="w-[22px] h-[22px] text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.2)]" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 7C3 5.89543 3.89543 5 5 5H9.58579C10.1162 5 10.6249 5.21071 11 5.58579L12.4142 7H19C20.1046 7 21 7.89543 21 9V18C21 19.1046 20.1046 20 19 20H5C3.89543 20 3 19.1046 3 18V7Z"
      fill="currentColor"
    />
    <path
      d="M3 10H21V18C21 19.1046 20.1046 20 19 20H5C3.89543 20 3 19.1046 3 18V10Z"
      fill="#60A5FA"
      opacity="0.3"
    />
  </svg>
);

const JrCrayonIcon = () => (
  <svg className="w-4 h-4 absolute right-3 pointer-events-none" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.5 3.5C17.3 2.7 18.7 2.7 19.5 3.5L20.5 4.5C21.3 5.3 21.3 6.7 20.5 7.5L8.5 19.5L3 21L4.5 15.5L16.5 3.5Z"
      fill="#FF9F1C"
      stroke="#B45309"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14.5 5.5L18.5 9.5" stroke="#FFF3D1" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M4.5 15.5L8.5 19.5" stroke="#B45309" strokeWidth="1.6" />
  </svg>
);

const WorkspaceNavbar = ({
  theme, // "dark" | "light" | "jr"
  isScratchTheme,
  isJuniorWorkspace = false,
  onBack,
  selectedChild,
  isStudentAccount,
  projectName,
  onProjectNameChange,
  onNewProject,
  onSaveProject,
  isSaving,
  isDirty,
  projectSizeBytes,
  onDownloadProject,
  onUploadLocal,
  onOpenBackpack,
}) => {
  const [isFileMenuOpen, setIsFileMenuOpen] = React.useState(false);
  const fileMenuRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (fileMenuRef.current && !fileMenuRef.current.contains(event.target)) {
        setIsFileMenuOpen(false);
      }
    };
    if (isFileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFileMenuOpen]);

  const resolvedTheme =
    theme || (isJuniorWorkspace ? "jr" : isScratchTheme ? "light" : "dark");
  const isJr = resolvedTheme === "jr";
  const isLight = resolvedTheme === "light";

  return (
    <div
      className={`px-4 flex items-center justify-between gap-3 flex-shrink-0 z-30 transition-colors duration-200 ${
        isJr
          ? "py-2 bg-gradient-to-r from-[#FFFDF7] via-[#FFF9EB] to-[#FFFDF7] border-b-2 border-[#E6C896] text-[#3D2610] shadow-[0_2px_8px_rgba(180,115,45,0.08)]"
          : isLight
          ? "py-2 bg-[#ffffff] border-b border-slate-200 text-slate-800 shadow-xs"
          : "py-2 bg-[#001428] border-b border-blue-900/60 text-white"
      }`}
    >
      {/* Left: Back Button & Child Display */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isSaving}
          title={isSaving ? "Saving project in progress... please wait" : "Back to Dashboard"}
          className={`flex items-center justify-center transition duration-150 cursor-pointer flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
            isJr
              ? "w-9 h-9 p-1.5 rounded-xl bg-gradient-to-b from-[#FFB703] to-[#FB8500] hover:brightness-105 text-white border-2 border-[#D96B00] shadow-[0_3px_0_#B85A00] active:translate-y-[2px] active:shadow-[0_1px_0_#B85A00]"
              : isLight
              ? "w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300/70"
              : "w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 text-white"
          }`}
        >
          {isJr ? (
            <JrBackIcon />
          ) : (
            <ArrowLeft
              className={`w-4 h-4 ${isLight ? "text-slate-700" : "text-white"}`}
            />
          )}
        </button>

        {selectedChild && !isStudentAccount && (
          <div
            className={`hidden sm:flex items-center gap-2 ${
              isJr
                ? "rounded-2xl px-3.5 py-1.5 bg-[#FFF4D9] text-[#5C3A14] border-2 border-[#F3C663] shadow-[0_2px_0_#E2B144] text-xs font-black"
                : isLight
                ? "rounded-xl px-3.5 py-1.5 text-xs shadow-xs bg-purple-100/70 text-purple-800 border border-purple-300 font-extrabold"
                : "rounded-xl px-3.5 py-1.5 text-xs shadow-xs bg-[#001428] text-white border border-blue-900/40 font-black"
            }`}
          >
            {isJr ? (
              <JrKidExplorerIcon />
            ) : (
              <User
                className={`w-3.5 h-3.5 ${
                  isLight ? "text-purple-700" : "text-purple-300"
                } flex-shrink-0`}
              />
            )}
            <span className="max-w-[110px] truncate">{selectedChild.name}</span>
          </div>
        )}
      </div>

      {/* Center: Project Naming & Size Meter */}
      <div className="flex-1 flex items-center justify-center gap-2 max-w-md mx-auto">
        <div
          className={`relative flex items-center w-full transition-all ${
            isJr
              ? "max-w-[265px] rounded-2xl bg-white hover:bg-[#FFFDF9] focus-within:bg-white border-2 border-[#E2C799] focus-within:border-[#FF8C00] shadow-[0_3px_0_#EBD5B3]"
              : isLight
              ? "max-w-[250px] rounded-lg bg-slate-100 hover:bg-slate-200/80 focus-within:bg-white border border-slate-300"
              : "max-w-[250px] rounded-lg bg-white/5 hover:bg-white/10 focus-within:bg-[#001A33]"
          }`}
        >
          <input
            type="text"
            value={projectName}
            onChange={(e) => onProjectNameChange(e.target.value)}
            className={`w-full bg-transparent outline-none text-center ${
              isJr
                ? "text-sm sm:text-base font-black px-4 py-1.5 text-[#3D2610] placeholder-[#B09778]"
                : isLight
                ? "text-sm font-bold px-3 py-1.5 text-slate-800 placeholder-slate-400"
                : "text-sm font-bold px-3 py-1.5 text-white placeholder-slate-400"
            }`}
            placeholder="Project Name"
          />
          {isJr ? (
            <JrCrayonIcon />
          ) : (
            <Pencil
              className={`w-3.5 h-3.5 absolute right-2 pointer-events-none ${
                isLight ? "text-slate-500" : "text-slate-400"
              }`}
            />
          )}
        </div>
      </div>

      {/* Right: Actions */}
      <div className={`flex items-center ${isJr ? "gap-2" : "gap-2"}`}>
        {/* Development-Only Project Size Inspector Dropdown */}
        <ProjectSizeInspector
          projectSizeBytes={projectSizeBytes}
          isJr={isJr}
          isLight={isLight}
        />

        {/* ========================================================================= */}
        {/* 1. FILES DROPDOWN TOOL */}
        {/* ========================================================================= */}
        <div className="relative" ref={fileMenuRef}>
          <button
            type="button"
            onClick={() => setIsFileMenuOpen((prev) => !prev)}
            title="Files menu"
            aria-label="Files menu"
            aria-expanded={isFileMenuOpen}
            className={`flex items-center transition cursor-pointer select-none ${
              isJr
                ? "justify-center flex-shrink-0 w-9 h-9 p-1.5 rounded-xl bg-gradient-to-b from-[#38BDF8] to-[#0284C7] hover:brightness-105 text-white border-2 border-[#0369A1] shadow-[0_3px_0_#075985] active:translate-y-[2px] active:shadow-[0_1px_0_#075985]"
                : isLight
                ? "gap-1.5 p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-extrabold shadow-xs bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300"
                : "gap-1.5 p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-extrabold shadow-xs bg-white/5 hover:bg-white/10 text-white border border-white/10"
            } ${isFileMenuOpen ? (isJr ? "brightness-105 translate-y-[1px] shadow-[0_2px_0_#075985]" : isLight ? "bg-slate-200" : "bg-white/15") : ""}`}
          >
            {isJr ? (
              <JrFolderIcon />
            ) : (
              <>
                <Folder className={`w-4 h-4 ${isLight ? "text-slate-600" : "text-slate-300"}`} />
                <span className="hidden sm:inline">Files</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${isLight ? "text-slate-500" : "text-slate-400"} ${isFileMenuOpen ? "rotate-180" : ""}`} />
              </>
            )}
          </button>

          {/* Files Dropdown Popover */}
          {isFileMenuOpen && (
            <div
              className={`absolute right-0 top-full mt-2 z-50 min-w-[210px] sm:min-w-[230px] ${
                isJr
                  ? "p-2 rounded-2xl bg-[#FFFDF7] border-2 border-[#E6C896] shadow-[0_8px_24px_rgba(180,115,45,0.2)]"
                  : isLight
                  ? "p-1.5 rounded-xl bg-white border border-slate-200 shadow-xl"
                  : "p-1.5 rounded-xl bg-[#0c0d12] border border-[#27272a] shadow-xl text-white"
              }`}
            >
              {/* Option 1: New Project */}
              <button
                type="button"
                onClick={() => {
                  setIsFileMenuOpen(false);
                  if (onNewProject) onNewProject();
                }}
                className={
                  isJr
                    ? "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-black text-[#78350F] hover:bg-[#FFF3D1] transition-colors cursor-pointer text-left"
                    : isLight
                    ? "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer text-left"
                    : "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-200 hover:bg-white/10 transition-colors cursor-pointer text-left"
                }
              >
                <FilePlus className={`w-4 h-4 ${isJr ? "text-[#0284C7]" : isLight ? "text-blue-600" : "text-blue-400"}`} />
                <span>New Project</span>
              </button>

              {/* Option 2: Save to Cloud */}
              <button
                type="button"
                disabled={isSaving}
                onClick={() => {
                  setIsFileMenuOpen(false);
                  if (onSaveProject) onSaveProject();
                }}
                className={
                  isJr
                    ? "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-black text-[#78350F] hover:bg-[#FFF3D1] transition-colors cursor-pointer text-left disabled:opacity-50"
                    : isLight
                    ? "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer text-left disabled:opacity-50"
                    : "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-200 hover:bg-white/10 transition-colors cursor-pointer text-left disabled:opacity-50"
                }
              >
                <Save className={`w-4 h-4 ${isJr ? "text-[#059669]" : isLight ? "text-purple-600" : "text-purple-400"}`} />
                <div className="flex items-center justify-between flex-1">
                  <span>Save to Cloud</span>
                  {isDirty && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse ml-2" />}
                </div>
              </button>

              {/* Option 3: Download */}
              <button
                type="button"
                onClick={() => {
                  setIsFileMenuOpen(false);
                  if (onDownloadProject) onDownloadProject();
                }}
                className={
                  isJr
                    ? "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-black text-[#78350F] hover:bg-[#FFF3D1] transition-colors cursor-pointer text-left"
                    : isLight
                    ? "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer text-left"
                    : "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-200 hover:bg-white/10 transition-colors cursor-pointer text-left"
                }
              >
                <Download className={`w-4 h-4 ${isJr ? "text-[#D97706]" : isLight ? "text-amber-600" : "text-amber-400"}`} />
                <span>Download</span>
              </button>

              {/* Option 4: Load from Local System */}
              <button
                type="button"
                onClick={() => {
                  setIsFileMenuOpen(false);
                  if (onUploadLocal) onUploadLocal();
                }}
                className={
                  isJr
                    ? "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-black text-[#78350F] hover:bg-[#FFF3D1] transition-colors cursor-pointer text-left"
                    : isLight
                    ? "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer text-left"
                    : "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-200 hover:bg-white/10 transition-colors cursor-pointer text-left"
                }
              >
                <Upload className={`w-4 h-4 ${isJr ? "text-[#0284C7]" : isLight ? "text-emerald-600" : "text-emerald-400"}`} />
                <span>Load from Local System</span>
              </button>
            </div>
          )}
        </div>

        {/* ========================================================================= */}
        {/* 2. SAVE TO CLOUD BUTTON */}
        {/* ========================================================================= */}
        <button
          type="button"
          onClick={onSaveProject}
          disabled={isSaving}
          title={isDirty ? "Save to Cloud (Unsaved Changes)" : "Save to Cloud"}
          aria-label="Save to Cloud"
          className={`flex items-center justify-center transition cursor-pointer disabled:opacity-50 relative ${
            isJr
              ? "flex-shrink-0 w-9 h-9 p-1.5 rounded-xl bg-gradient-to-b from-[#34D399] to-[#10B981] hover:brightness-105 text-white border-2 border-[#047857] shadow-[0_3px_0_#065F46] active:translate-y-[2px] active:shadow-[0_1px_0_#065F46]"
              : isLight
              ? "p-2 rounded-lg shadow-sm bg-purple-600 text-white hover:bg-purple-500 shadow-purple-500/20"
              : "p-2 rounded-lg shadow-sm bg-white/5 hover:bg-blue-600/80 text-white"
          }`}
        >
          {isDirty && !isSaving && (
            <span
              className={`absolute bg-red-500 border-2 border-white rounded-full animate-pulse ${
                isJr ? "-top-1.5 -right-1.5 w-3.5 h-3.5" : "-top-1 -right-1 w-2.5 h-2.5"
              }`}
            />
          )}
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : isJr ? (
            <FaFloppyDisk className="w-[22px] h-[22px] text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.2)]" />
          ) : (
            <Save className="w-4 h-4 text-white" />
          )}
        </button>

        {/* My Backpack Button */}
        <button
          type="button"
          onClick={onOpenBackpack}
          title="My Backpack"
          aria-label="My Backpack"
          className={`flex items-center transition cursor-pointer ${
            isJr
              ? "justify-center flex-shrink-0 w-9 h-9 p-1.5 rounded-xl bg-gradient-to-b from-[#FB923C] to-[#EA580C] hover:brightness-105 text-white border-2 border-[#C2410C] shadow-[0_3px_0_#9A3412] active:translate-y-[2px] active:shadow-[0_1px_0_#9A3412]"
              : isLight
              ? "gap-1.5 p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-extrabold shadow-xs bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-300"
              : "gap-1.5 p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-extrabold shadow-xs bg-white/5 hover:bg-purple-600/80 text-white"
          }`}
        >
          {isJr ? (
            <MdBackpack className="w-[23px] h-[23px] text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.2)]" />
          ) : (
            <>
              <svg
                className={`w-4 h-4 ${isLight ? "text-purple-600" : "text-purple-300"}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10z" />
                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                <path d="M8 21v-5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v5" />
                <path d="M8 10h8" />
              </svg>
              <span className="hidden sm:inline">Backpack</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default WorkspaceNavbar;
